/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "@tanstack/react-query";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import input from "../../../../../assets/styles/components/input.module.css";
import { fetchPaymentTypes } from "../../../../../services/financial";
import { loadAllClients } from "../../../../../services/customers";
import type { IFinancialFilter } from "../../../../../types/financial";
import { formatDate } from "../../../../../utils/useMask";
import Button from "../../../../../components/Button/Button";
import SelectChartOfAccounts from "../../CashFlow/SelectChartOfAccounts";
import SelectCostCenter from "../../CashFlow/SelectCostCenter";
import SelectFinanceAccount from "../../CashFlow/SelectFinanceAccount";
import InputSuspense from "../../../../../components/InputSuspense/InputSuspense";
import ModalGeneric from "../../../../../components/ModalGeneric/ModalGeneric";
import styles from "./AddFinancialEntryModal.module.css";

interface ClienteSelect {
  uid_cliente: string;
  nome: string;
}

interface PaymentTypeSelect {
  uid_tipo_pagamento: string;
  titulo: string;
}

type props = {
  onClose: () => void;
  setValuesInput: (filter: IFinancialFilter) => void;
  valuesInput: IFinancialFilter;
};

const PERIOD_OPTIONS = [
  { label: "Selecione um período", value: "" },
  { label: "Essa semana", value: "this_week" },
  { label: "Semana que vem", value: "next_week" },
  { label: "Semana passada", value: "last_week" },
  { label: "Esse Mês", value: "this_month" },
  { label: "Mês que vem", value: "next_month" },
  { label: "Mês passado", value: "last_month" },
  { label: "Esse ano", value: "this_year" },
  { label: "Ano que vem", value: "next_year" },
  { label: "Ano passado", value: "last_year" },
];

const FinancialFilterModal = ({
  onClose,
  setValuesInput,
  valuesInput,
}: props) => {
  const initialTipo = "receber";

  const [dataFilter, setDataFilter] = useState<
    IFinancialFilter & { period?: string }
  >({
    uid_cliente: valuesInput.uid_cliente || null,
    status: valuesInput.status || "",
    uid_tipo_pagamento: valuesInput.uid_tipo_pagamento || null,
    date_initial: valuesInput.date_initial,
    date_final: valuesInput.date_final,
    tipo: initialTipo,
    period: (valuesInput as any).period || null,
    uid_conta_financeira: valuesInput.uid_conta_financeira || null,
    uid_plano_contas: valuesInput.uid_plano_contas || null,
    uid_centro_custo: valuesInput.uid_centro_custo || null,
  });

  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [isActiveClient, setIsActiveClient] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState("");

  const { data: clientsData, isLoading: isLoadingClients } = useQuery<any, Error>({
    queryKey: ["allClientsForFilter", clientSearchTerm],
    queryFn: () => loadAllClients(clientSearchTerm, 1),
  });

  const { data: paymentTypesData, isLoading: isLoadingPaymentTypes } = useQuery<
    PaymentTypeSelect[],
    Error
  >({
    queryKey: ["paymentTypesForFilter"],
    queryFn: fetchPaymentTypes,
  });

  const clientsForSelect: ClienteSelect[] = useMemo(() => {
    return clientsData?.data?.clients?.clients || [];
  }, [clientsData]);

  const paymentTypesList: PaymentTypeSelect[] = paymentTypesData || [];

  useEffect(() => {
    if (dataFilter.uid_cliente) {
      const selectedClient = clientsForSelect.find(
        (c) => c.uid_cliente === dataFilter.uid_cliente,
      );
      if (selectedClient) {
        if (selectedClientName !== selectedClient.nome) {
          setSelectedClientName(selectedClient.nome);
        }
      } else {
        if (selectedClientName === "") {
          setSelectedClientName("Cliente Selecionado");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientsForSelect, dataFilter.uid_cliente]);

  const handlePeriodChange = (period: string) => {
    const today = new Date();
    const first = today.getDate() - today.getDay();

    const getOffsetDate = (offset: number) => {
      const d = new Date();
      d.setDate(first + offset);
      return formatDate(d);
    };

    let start: string | undefined = "";
    let end: string | undefined = "";

    switch (period) {
      case "this_week":
        start = getOffsetDate(0);
        end = getOffsetDate(6);
        break;
      case "next_week":
        start = getOffsetDate(7);
        end = getOffsetDate(13);
        break;
      case "last_week":
        start = getOffsetDate(-7);
        end = getOffsetDate(-1);
        break;
      case "this_month":
        start = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
        end = formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        break;
      case "next_month":
        start = formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 1));
        end = formatDate(new Date(today.getFullYear(), today.getMonth() + 2, 0));
        break;
      case "last_month":
        start = formatDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
        end = formatDate(new Date(today.getFullYear(), today.getMonth(), 0));
        break;
      case "this_year":
        start = formatDate(new Date(today.getFullYear(), 0, 1));
        end = formatDate(new Date(today.getFullYear(), 11, 31));
        break;
      case "next_year":
        start = formatDate(new Date(today.getFullYear() + 1, 0, 1));
        end = formatDate(new Date(today.getFullYear() + 1, 11, 31));
        break;
      case "last_year":
        start = formatDate(new Date(today.getFullYear() - 1, 0, 1));
        end = formatDate(new Date(today.getFullYear() - 1, 11, 31));
        break;
      default:
        return null;
    }

    setDataFilter((prev) => ({
      ...prev,
      period: period,
      date_initial: start,
      date_final: end,
    }));
  };

  const handleSelectClient = (nome: string, uid: string) => {
    setDataFilter((prev) => ({ ...prev, uid_cliente: uid }));
    setSelectedClientName(nome);
    setClientSearchTerm("");
    setIsActiveClient(false);
  };

  const handleClientInput = (inputValue: string) => {
    setClientSearchTerm(inputValue);
    setSelectedClientName(inputValue);
    setDataFilter((prev) => ({ ...prev, uid_cliente: "" }));
    setIsActiveClient(true);
  };

  const clientOptionsMemo = useMemo(() => {
    if (isLoadingClients)
      return <p className={styles.dropDownLoading}>Carregando...</p>;

    if (!clientsForSelect || clientsForSelect.length === 0) {
      return (
        <p className={styles.dropDownNoResults}>Nenhum cliente encontrado.</p>
      );
    }

    return clientsForSelect.map((item) => (
      <p
        key={item.uid_cliente}
        className={styles.dropDownItem}
        onClick={() => handleSelectClient(item.nome, item.uid_cliente)}
      >
        {item.nome}
      </p>
    ));
  }, [clientsForSelect, isLoadingClients]);

  const handleFilter = () => {
    const finalFilters = {
      ...dataFilter,
      tipo: dataFilter.tipo || initialTipo,
    };

    setValuesInput(finalFilters as IFinancialFilter);
    onClose();
  };

  const handleChange = (value: string, field: keyof IFinancialFilter) => {
    setDataFilter((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <ModalGeneric
      containerClass={styles.container}
      contentClass={styles.containerModalFilter}
      title="Filtrar Contas a Receber"
      onClick={onClose}
    >
      <div className={styles.containerFilter}>
        <InputSuspense
          label="Cliente"
          placeholder="Pesquisar cliente"
          value={selectedClientName}
          onChange={(e) => handleClientInput(e.target.value)}
          isActiveDropDown={isActiveClient}
          setIsActiveDropDown={setIsActiveClient}
          option={clientOptionsMemo}
          name="uid_cliente_input"
        />
        <input type="hidden" value={dataFilter.uid_cliente || ""} readOnly />

        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <p>Tipo de Pagamento</p>
            <select
              className={input.input}
              value={dataFilter.uid_tipo_pagamento || ""}
              onChange={(e) =>
                setDataFilter((prev) => ({ ...prev, uid_tipo_pagamento: e.target.value }))
              }
              disabled={isLoadingPaymentTypes}
            >
              <option value="">Todos Tipos</option>
              {paymentTypesList.map((item) => (
                <option key={item.uid_tipo_pagamento} value={item.uid_tipo_pagamento}>
                  {item.titulo}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <p>Status</p>
            <select
              className={input.input}
              value={dataFilter.status || ""}
              onChange={(e) =>
                setDataFilter((prev) => ({
                  ...prev,
                  status: e.target.value as IFinancialFilter["status"],
                }))
              }
            >
              <option value="">Todos Status</option>
              <option value="aberto">Em Aberto</option>
              <option value="pago">Pago</option>
              <option value="atrasado">Atrasado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>

        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <SelectFinanceAccount
              handleChangeAccount={(value) => handleChange(value, "uid_conta_financeira")}
              value={dataFilter.uid_conta_financeira || ""}
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectCostCenter
              value={dataFilter.uid_centro_custo || ""}
              handleChangeCostCenter={(value) => handleChange(value, "uid_centro_custo")}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <SelectChartOfAccounts
              value={dataFilter.uid_plano_contas || ""}
              handleChangeChartOfAccount={(value) => handleChange(value, "uid_plano_contas")}
              type="ambos"
            />
          </div>
        </div>

        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <p>Período</p>
            <select
              className={input.input}
              value={dataFilter.period || ""}
              onChange={(e) => handlePeriodChange(e.target.value)}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <p>Data Inicial (Vencimento)</p>
            <input
              type="date"
              className={input.input}
              value={dataFilter.date_initial || ""}
              onChange={(e) =>
                setDataFilter((prev) => ({
                  ...prev,
                  date_initial: e.target.value,
                  period: "",
                }))
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <p>Data Final (Vencimento)</p>
            <input
              type="date"
              className={input.input}
              value={dataFilter.date_final || ""}
              onChange={(e) =>
                setDataFilter((prev) => ({
                  ...prev,
                  date_final: e.target.value,
                  period: "",
                }))
              }
            />
          </div>
        </div>

        <Button
          variant="contain"
          onClick={handleFilter}
          disabled={isLoadingClients || isLoadingPaymentTypes}
        >
          {isLoadingClients || isLoadingPaymentTypes ? "Carregando..." : "Filtrar"}
        </Button>
      </div>
    </ModalGeneric>
  );
};

export default FinancialFilterModal;
